/** Name / description / category / tags for a team block — shared by Save as block and Edit details. */
import { useId, useState, type CSSProperties } from 'react'
import { colors, fontSize, radius } from '../../styles/theme'
import { TEAM_BLOCK_CATEGORIES } from '../../blocks/teamBlocks'
import type { BlockDetails } from '../../services/blocksApiService'

export interface BlockDetailsValue {
  name: string
  description: string
  category: string
  tags: string[]
}

export const emptyBlockDetails = (): BlockDetailsValue => ({ name: '', description: '', category: '', tags: [] })

export const MAX_TAGS = 10

/** The request shape: trimmed, blanks omitted. */
export function toBlockDetails(v: BlockDetailsValue): BlockDetails {
  return {
    name: v.name.trim(),
    description: v.description.trim() || undefined,
    category: v.category.trim() || undefined,
    tags: v.tags.length ? v.tags : undefined,
  }
}

export function validateBlockDetails(v: BlockDetailsValue): string | null {
  const name = v.name.trim()
  if (!name) return 'Name is required.'
  if (name.length > 120) return 'Name is at most 120 characters.'
  if (v.description.trim().length > 500) return 'Description is at most 500 characters.'
  if (v.category.trim().length > 40) return 'Category is at most 40 characters.'
  return null
}

export const fieldStyle: CSSProperties = {
  padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm,
  fontSize: fontSize.sm, background: colors.surface, color: colors.text, boxSizing: 'border-box', width: '100%',
}

export const labelStyle: CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4, fontSize: fontSize.sm, color: colors.textSecondary,
}

export default function BlockDetailsFields({ value, onChange, categories = [], autoFocus }: {
  value: BlockDetailsValue
  onChange: (v: BlockDetailsValue) => void
  /** The team's existing categories, offered alongside the standard ones. */
  categories?: string[]
  autoFocus?: boolean
}) {
  const listId = useId()
  const [tagDraft, setTagDraft] = useState('')
  const suggestions = [...new Set([...TEAM_BLOCK_CATEGORIES, ...categories.filter(Boolean)])]
  const set = (patch: Partial<BlockDetailsValue>) => onChange({ ...value, ...patch })

  function addTag(raw: string) {
    const tag = raw.trim().slice(0, 30)
    setTagDraft('')
    if (!tag || value.tags.length >= MAX_TAGS || value.tags.some(t => t.toLowerCase() === tag.toLowerCase())) return
    set({ tags: [...value.tags, tag] })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <label style={labelStyle}>
        Name
        <input autoFocus={autoFocus} value={value.name} maxLength={120} required onChange={e => set({ name: e.target.value })} style={fieldStyle} />
      </label>
      <label style={labelStyle}>
        Description
        <textarea value={value.description} maxLength={500} rows={2} onChange={e => set({ description: e.target.value })} style={{ ...fieldStyle, resize: 'vertical' }} />
      </label>
      <label style={labelStyle}>
        Category
        <input list={listId} value={value.category} maxLength={40} placeholder="e.g. Legal" onChange={e => set({ category: e.target.value })} style={fieldStyle} />
        <datalist id={listId}>{suggestions.map(c => <option key={c} value={c} />)}</datalist>
      </label>
      <div style={labelStyle}>
        <label htmlFor={`${listId}-tags`}>Tags</label>
        <div style={{ ...fieldStyle, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
          {value.tags.map(t => (
            <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 4px 1px 8px', borderRadius: radius.full, background: colors.surfaceMist, color: colors.text, fontSize: fontSize.xs }}>
              {t}
              <button type="button" aria-label={`Remove tag ${t}`} onClick={() => set({ tags: value.tags.filter(x => x !== t) })}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: colors.textMuted, padding: '0 2px', fontSize: fontSize.sm, lineHeight: 1 }}>×</button>
            </span>
          ))}
          <input
            id={`${listId}-tags`}
            value={tagDraft}
            disabled={value.tags.length >= MAX_TAGS}
            placeholder={value.tags.length ? '' : 'Type and press Enter'}
            onChange={e => {
              const v = e.target.value
              if (v.endsWith(',')) addTag(v.slice(0, -1))
              else setTagDraft(v)
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); addTag(tagDraft) }
              else if (e.key === 'Backspace' && !tagDraft && value.tags.length) set({ tags: value.tags.slice(0, -1) })
            }}
            onBlur={() => { if (tagDraft.trim()) addTag(tagDraft) }}
            style={{ flex: '1 0 80px', border: 'none', outline: 'none', fontSize: fontSize.sm, background: 'transparent', color: colors.text, padding: 2 }}
          />
        </div>
      </div>
    </div>
  )
}
